import { QUERY_STRING_KEY } from '../constants/app';
import {
  getLocalStorage,
  getSessionStorage,
  isCollectionPage,
  isProductPage,
  isSearchPage,
  setLocalStorage,
  setSessionStorage,
} from '../utils';
import { getAttentiveInfo, initReceiveEventAttentive, isAttentiveActive } from '../utils/attentive';
import { isKlaviyoActive } from '../utils/integration';
import { startSession } from '../workers/workers-provider';
import {
  ACTION,
  ANALYTICS_URL,
  BOOST_CART_TOKEN,
  ATTENTIVE_BOOST_EVENT,
  BOOST_SD_PRODUCT_IDS_CLICKED,
  DEFAULT_SELECTOR,
  DYNAMIC_BUNDLE_TYPE,
  MAP_PATH_CLICK,
  PLACEMENT,
  PopupBundlePlacement,
  PRE_ACTION,
  prefix,
  RECOMMEND_ACTIVE,
  REQUEST_ID_PRE_ORDER_VIEW,
  SHOPIFY_CART_TOKEN,
  USER_ACTION,
} from './constants';
import {
  addEventKeyPressForSearching,
  buildDataDynamicBundle,
  buildGeneralData,
  setProductExtraInfo,
  checkActiveElementWhiteList,
  checkSendEventKlaviyoViewCollection,
  checkSendEventWhenLoadPage,
  createBoostTokenUpdateCartAttribute,
  generateRIDPreOrder,
  getExtraData,
  getModalQuickView,
  getParamsHistory,
  getPlacementBundle,
  getProductIdFromQuickView,
  handleFilterOptionItemButtonClick,
  handleKlaviyo,
  identifyUser,
  isClickAddToCart,
  isExcludeAddToCart,
  postData,
  saveSessionStorageClicked,
  sendKlaviyoEventChangeRangeSlider,
  sendKlaviyoEventInCollectionSearch,
} from './main-thread';

window.boostAnalytic = DEFAULT_SELECTOR;

/**
 * @type {import('./index.d').sendEvent}
 */
function sendEvent(data, _activeElement) {
  const runtime = window.boostWidgetIntegrationConfig?.analytics?.runtime || 'worker'; // "worker" or "main-thread"

  // Prevent sent event data when view product have not productId - case bundle as product
  if (!data.pid && data.u === USER_ACTION.VIEW_PRODUCT) {
    return;
  }

  if (data.a === ACTION.RECOMMEND) {
    // not send qs when action recommend exclude bundle
    if (!data.actionBundle) {
      data.qs = null;
    }
  }
  if (data.a === ACTION.RECOMMEND) {
    const recommendActive = getLocalStorage(RECOMMEND_ACTIVE) || {};
    data = {
      ...data,
      ...recommendActive,
    };
  }

  setLocalStorage(PRE_ACTION, data.a);

  // prevent all event of widget which widget not come from turbo
  if (!data.rid) return;

  // buy now it will reset attribute cart but not reset cart_token so pass cart_token have been generated when call updated cart
  // cart_token only change when it be done payment.
  if (USER_ACTION.BUY_NOW === data.u) {
    data.ct = getLocalStorage(SHOPIFY_CART_TOKEN);
  }

  if (runtime === 'worker' && data.u !== USER_ACTION.BUY_NOW) {
    try {
      startSession('analytics').send({ action: 'sendEvent', data, extraData: getExtraData() });
    } catch (error) {
      console.log('error sendEvent', error);
    }
  } else {
    // new approach have generated cart_token so don't need refresh cart token before sendEvent
    postData(ANALYTICS_URL, data);
  }
}

/**
 * @type {import('./index.d').checkViewPreOrder}
 */
function checkViewPreOrder(pid = 0) {
  let productId = pid;
  if (isProductPage()) {
    productId = window.boostWidgetIntegration?.generalSettings?.product_id || window.__st?.rid;
  }

  if (!productId) return;

  let attempts = 0;
  const maxAttempts = 100;

  const intervalId = setInterval(() => {
    // find element have class boost-sd__preorder-title
    const hasPreOrderBlock = document.querySelector(
      '.boost-sd__preorder-option .boost-sd__preorder-title'
    );

    if (hasPreOrderBlock) {
      clearInterval(intervalId);
      const data = buildGeneralData({ u: USER_ACTION.VIEW_PRODUCT_PRE_ORDER, pid: productId });

      const clicked = getSessionStorage(BOOST_SD_PRODUCT_IDS_CLICKED) || {};
      if (clicked[productId] && clicked[productId] !== 'none') {
        data.rid = clicked[productId];
        data.a = clicked[`${productId}_action`];
      } else {
        data.rid = generateRIDPreOrder();
        setSessionStorage(REQUEST_ID_PRE_ORDER_VIEW, data.rid);
        data.a = 'other';
      }

      sendEvent(data);
    }

    // If not found and re-try maxAttempts -> stop
    attempts++;
    if (attempts >= maxAttempts) {
      clearInterval(intervalId);
    }
  }, 100);
}

/**
 * @type {import('./index.d').checkAndUpdateBoostCartToken}
 */
function checkAndUpdateBoostCartToken() {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      // BOOST_CART_TOKEN.SHORT_KEY: boost_cart_token
      if (!cart?.attributes?.[BOOST_CART_TOKEN.SHORT_KEY]) {
        createBoostTokenUpdateCartAttribute();
      }

      if (cart?.attributes?.[BOOST_CART_TOKEN.SHORT_KEY]) {
        // Update localStorage with the boost cart token
        setLocalStorage(BOOST_CART_TOKEN.STORAGE_KEY, cart.attributes[BOOST_CART_TOKEN.SHORT_KEY]);
      }
    });
}
/**
 * @type {import('./index.d').initAnalytic}
 */
export function initAnalytic() {
  if (isSearchPage()) {
    setLocalStorage(QUERY_STRING_KEY, getParamsHistory());
  }

  // For Klaviyo
  identifyUser();
  checkSendEventKlaviyoViewCollection();
  addEventKeyPressForSearching();
  // End for Klaviyo

  // For Attentive
  initReceiveEventAttentive();
  // End for Attentive

  window.addEventListener('debounce-in-collection-search', sendKlaviyoEventInCollectionSearch);
  window.addEventListener('change-range-slider', sendKlaviyoEventChangeRangeSlider);
  window.addEventListener('re-view-collection', checkSendEventKlaviyoViewCollection);

  checkSendEventWhenLoadPage();
  console.log('Analytic register events - Version SSR');
  document.addEventListener('click', onClickTrackingEvent, true);

  checkAndUpdateBoostCartToken();

  checkViewPreOrder();
}

/**
 * @type {import('./index.d').onClickTrackingEvent}
 */
function onClickTrackingEvent(event) {
  if (!event || !event.target) return;
  let recommendData = {};

  const activeElement = event.target;
  let action = isSearchPage() ? ACTION.SEARCH : isCollectionPage ? ACTION.FILTER : '';
  let productId = '';
  let actionBundle = false;
  let placementBundle = '';

  if (isProductPage()) {
    action = getLocalStorage(PRE_ACTION) || ACTION.FILTER;
    productId = window.boostWidgetIntegration?.generalSettings?.product_id || '';
    placementBundle = PLACEMENT.product_page;
  }

  if (isSearchPage()) {
    placementBundle = PLACEMENT.search_page;
  }

  if (activeElement.closest(window.boostAnalytic.suggest || DEFAULT_SELECTOR.suggest)) {
    action = ACTION.SUGGEST;
    placementBundle = PLACEMENT.instant_search;
  }

  if (activeElement.closest(window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend)) {
    action = ACTION.RECOMMEND;
    const recommendationElement = activeElement.closest(
      window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend
    );

    if (recommendationElement && recommendationElement.id) {
      recommendData = getLocalStorage(`boostSdRecommend-${recommendationElement.id}`) || {};

      // data recommendation dynamic bundle
      if (activeElement.closest(`.${prefix}recommendation--dynamic`)) {
        actionBundle = true;
        const dataDynamicBundle = buildDataDynamicBundle(recommendationElement);
        recommendData = {
          ...recommendData,
          ...dataDynamicBundle,
          placement: getPlacementBundle(placementBundle),
        };

        const productOfBundle = activeElement.closest(`.${prefix}recommendation-bundle-image-item`);
        // current: only click image or title then count click product bundle
        const checkWhiteListCountAsProductBundle = checkActiveElementWhiteList(activeElement, [
          `.boost-sd__recommendation-bundle-item-title`,
          `.boost-sd__recommendation-bundle-link-image`,
        ]);
        if (
          productOfBundle &&
          productOfBundle?.dataset?.productId &&
          checkWhiteListCountAsProductBundle
        ) {
          productId = productOfBundle?.dataset?.productId;
        }
      }

      setLocalStorage(RECOMMEND_ACTIVE, recommendData);
    }
  }

  const recommendationActive = getLocalStorage(RECOMMEND_ACTIVE);
  const preAction = getLocalStorage(PRE_ACTION);
  if (preAction === ACTION.RECOMMEND && recommendationActive.rt === DYNAMIC_BUNDLE_TYPE) {
    actionBundle = true;
    recommendData = recommendationActive;
  }

  const data = { a: action, pid: productId, actionBundle };

  // listener event from quick view
  window.addEventListener('boost-sd-open-quick-view', function bindDataActionToQuickView(e) {
    const quickViewModal = getModalQuickView();

    quickViewModal?.setAttribute('data-action-analytic', data?.a);
    if (e?.detail?.pid) quickViewModal?.setAttribute('data-product-id', e?.detail?.pid);

    window.removeEventListener('boost-sd-open-quick-view', bindDataActionToQuickView);

    checkViewPreOrder(e?.detail?.pid);
  });

  let isSent = false;

  if (
    !isExcludeAddToCart(activeElement) &&
    (activeElement.closest(window.boostAnalytic.addToCart || DEFAULT_SELECTOR.addToCart) ||
      isClickAddToCart(activeElement))
  ) {
    isSent = true;
    if (!data.pid) {
      data.pid = getProductIdFromQuickView(activeElement);
    }

    data.u = USER_ACTION.ADD_TO_CART;
    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }
  } else if (activeElement.closest(window.boostAnalytic.buyNow || DEFAULT_SELECTOR.buyNow)) {
    isSent = true;
    if (!data.pid) {
      data.pid = getProductIdFromQuickView(activeElement);
    }

    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }

    data.u = USER_ACTION.BUY_NOW;
    const buyNowButton = activeElement.closest(
      window.boostAnalytic.buyNow || DEFAULT_SELECTOR.buyNow
    );

    // if classList buyNowButton contains ourBuyNow then it is click from our buy it now app
    if (
      buyNowButton?.classList.contains(window.boostAnalytic.ourBuyNow || DEFAULT_SELECTOR.ourBuyNow)
    ) {
      data.bct = getLocalStorage(BOOST_CART_TOKEN.STORAGE_KEY);
    }
  } else if (activeElement.closest(window.boostAnalytic.quickView || DEFAULT_SELECTOR.quickView)) {
    isSent = true;

    data.u = USER_ACTION.QUICK_VIEW;
  } else if (activeElement.closest(window.boostAnalytic.recommend || DEFAULT_SELECTOR.recommend)) {
    isSent = true;
  }

  // if is pre-order then priority sent pre-order than view.
  if (activeElement.closest(window.boostAnalytic.preOrder || DEFAULT_SELECTOR.preOrder)) {
    isSent = true;
    if (!data.pid) {
      data.pid = getProductIdFromQuickView(activeElement);
    }

    data.u = USER_ACTION.ATC_PRE_ORDER;
    const actionFromQuickView = getModalQuickView()?.dataset.actionAnalytic;
    if (actionFromQuickView) {
      data.a = actionFromQuickView;
    }
  }

  // Place Latest Click into Product Item - assign productId
  if (activeElement.closest(window.boostAnalytic.productItem || DEFAULT_SELECTOR.productItem)) {
    isSent = true;
    const productItem = activeElement.closest(
      window.boostAnalytic.productItem || DEFAULT_SELECTOR.productItem
    );
    const productId = productItem?.dataset?.id || productItem?.getAttribute('id');
    const variantId = productItem?.dataset?.productId; // product-id will is variant-id in turbo version

    data.pid = productId;
    data.vid = variantId || ''; // add variant_id for turbo version

    saveSessionStorageClicked(productId, 'none');
  }

  // if product page get productId from general Settings
  if (!data.pid && window.boostSDData?.product?.id) {
    data.pid = window.boostSDData?.product?.id;
  }

  const isClickPopupBundle =
    activeElement.closest('.boost-sd__recommendation-dynamic-show-popup') ||
    activeElement.closest('.boost-sd__product-item--embedded-bundle') ||
    activeElement.closest('.boost-sd__suggestion-queries-item--embedded-bundle');

  if (isClickPopupBundle) {
    actionBundle = true;
  }

  if (actionBundle) {
    // save last placement of bundle when open popup
    if ((action = ACTION.SUGGEST)) {
      const _placementPopup = PLACEMENT.instant_search;
      setSessionStorage(PopupBundlePlacement, _placementPopup);
    }

    data.placementBundle = getPlacementBundle(placementBundle);

    if (
      data.u === USER_ACTION.ADD_TO_CART &&
      activeElement.closest('.boost-sd__recommendation-bundle-button')
    ) {
      delete data.pid;
    }
  }

  handleFilterOptionItemButtonClick(activeElement);

  // not build data, sent if isSent = false
  if (!isSent) return;

  if (isSent) {
    const buildData = buildGeneralData(data, { ...recommendData, actionBundle });
    if (data.bct) {
      buildData.bct = data.bct;
    }

    const clonedBuildData = { ...buildData };
    if (isAttentiveActive()) {
      setProductExtraInfo(activeElement, clonedBuildData);
      const attentiveInfo = getAttentiveInfo(clonedBuildData);
      if (attentiveInfo.event_name === ATTENTIVE_BOOST_EVENT.ADD_TO_CART) {
        clonedBuildData.add_to_cart_context =
          clonedBuildData.a === ACTION.RECOMMEND ? clonedBuildData.wid : clonedBuildData.pg;
      }
      if (attentiveInfo.event_name === ATTENTIVE_BOOST_EVENT.CLICK_SEARCH_RESULT) {
        clonedBuildData.path_click = MAP_PATH_CLICK[clonedBuildData.a] || '';
      }
      clonedBuildData.attentive_info = attentiveInfo;
    }
    sendEvent(clonedBuildData, activeElement);

    if (window.klaviyo && isKlaviyoActive()) {
      handleKlaviyo(activeElement, buildData);
    }
  }
}
